// src/components/ComparePage.jsx
// Side-by-side round comparison. Tier-gated slot count: free=2, pro=6, elite=15.
import React, { useState, useMemo, useEffect } from 'react'
import { useAuth } from '../lib/AuthContext'
import { getAmmoForProfile } from '../data/ammoDB'

const TIER_LIMITS = { free: 2, pro: 6, elite: 15 }

const ROWS = [
  { key: 'mv',          label: 'MV (corrected)', fmt: r => `${r.corrected_mv || r.base_mv} fps` },
  { key: 'bc',          label: 'BC (G1)',        fmt: r => r.bc_g1?.toFixed(3) ?? '—' },
  { key: 'penetration', label: 'Penetration',    fmt: r => ratingDots(r.penetration_rating) },
  { key: 'barrier',     label: 'Barrier',        fmt: r => ratingDots(r.barrier_rating) },
  { key: 'wound',       label: 'Wound channel',  fmt: r => r.wound_channel || '—' },
  { key: 'eth',         label: 'Ethical range',  fmt: r => r.ethical_range_yds ? `${r.ethical_range_yds} yd` : '—' },
  { key: 'fbi',         label: 'FBI tested',     fmt: r => r.fbi_protocol ? '✓' : '—' },
]

function ratingDots(n) {
  if (n == null) return '—'
  return '●'.repeat(n) + '○'.repeat(Math.max(0, 5 - n))
}

export default function ComparePage({ activeProfile, onUpgrade }) {
  const { tier } = useAuth()
  const limit = TIER_LIMITS[tier] || TIER_LIMITS.free

  const ammoList = useMemo(
    () => activeProfile ? getAmmoForProfile(activeProfile, 'all') : [],
    [activeProfile]
  )

  const [slots, setSlots] = useState([])
  const [pickerOpenAt, setPickerOpenAt] = useState(null)

  useEffect(() => {
    setSlots([])
    setPickerOpenAt(null)
  }, [activeProfile?.id])

  function pickRound(slotIndex, round) {
    const next = [...slots]
    next[slotIndex] = round
    setSlots(next)
    setPickerOpenAt(null)
  }

  function removeRound(slotIndex) {
    const next = slots.filter((_, i) => i !== slotIndex)
    setSlots(next)
  }

  if (!activeProfile) {
    return (
      <div style={s.empty}>
        <div style={s.emptyIcon}>◉</div>
        <div style={s.emptyTitle}>No rifle profile selected</div>
        <div style={s.emptySub}>Select a profile under the Rifles tab to compare ammo.</div>
      </div>
    )
  }

  const totalSlotsToShow = Math.max(slots.length + 1, 2)
  const pickedIds = new Set(slots.map(r => r?.id).filter(Boolean))

  return (
    <div style={s.page}>
      <div style={s.profilePill}>
        <span style={s.pillDot} />
        <span style={s.pillText}>{activeProfile.nickname} · {activeProfile.caliber} · {activeProfile.barrel_length}"</span>
      </div>

      <div style={s.section}>
        <div style={s.sectionLabel}>
          Compare Rounds
          <span style={s.tierTag}>{tier.toUpperCase()} · UP TO {limit}</span>
        </div>

        <div style={s.slotGrid}>
          {Array.from({ length: totalSlotsToShow }).map((_, i) => {
            const round = slots[i]
            const locked = i >= limit
            if (locked) return <LockedSlot key={i} onUpgrade={onUpgrade} />
            if (round) {
              return (
                <SlotCard
                  key={i}
                  round={round}
                  onRemove={() => removeRound(i)}
                />
              )
            }
            const open = pickerOpenAt === i
            return (
              <EmptySlot
                key={i}
                open={open}
                ammoList={ammoList.filter(a => !pickedIds.has(a.id))}
                onOpen={() => setPickerOpenAt(open ? null : i)}
                onPick={r => pickRound(i, r)}
              />
            )
          })}
        </div>
      </div>

      {slots.length >= 2 && (
        <div style={s.section}>
          <div style={s.sectionLabel}>Side-by-side</div>
          <div style={s.tableScroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.thLabel}></th>
                  {slots.map((r, i) => (
                    <th key={i} style={s.thRound}>
                      <div style={s.thRoundName}>{r.name}</div>
                      <div style={s.thRoundMeta}>{r.grains}gr · {r.manufacturer}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(row => (
                  <tr key={row.key}>
                    <td style={s.tdLabel}>{row.label}</td>
                    {slots.map((r, i) => (
                      <td key={i} style={s.tdVal}>{row.fmt(r)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {slots.length < 2 && (
        <div style={s.hint}>
          Pick at least two rounds to see the comparison table.
        </div>
      )}
    </div>
  )
}

function EmptySlot({ open, ammoList, onOpen, onPick }) {
  return (
    <div style={s.emptySlot}>
      <button style={s.emptyBtn} onClick={onOpen}>
        {open ? '× Close picker' : '+ Add a round'}
      </button>
      {open && (
        <div style={s.picker}>
          {ammoList.length === 0 ? (
            <div style={s.pickerEmpty}>No more rounds available for this caliber.</div>
          ) : (
            ammoList.map(a => (
              <button key={a.id} style={s.pickerItem} onClick={() => onPick(a)}>
                <div style={s.pickerItemName}>{a.name}</div>
                <div style={s.pickerItemMeta}>{a.grains}gr · {a.bullet_type} · {a.manufacturer}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function SlotCard({ round, onRemove }) {
  return (
    <div style={s.slotCard}>
      <button style={s.removeBtn} onClick={onRemove} aria-label="Remove">✕</button>
      <div style={s.slotName}>{round.name}</div>
      <div style={s.slotMeta}>{round.grains}gr · {round.bullet_type}</div>
      <div style={s.slotMeta}>{round.manufacturer}</div>
    </div>
  )
}

function LockedSlot({ onUpgrade }) {
  return (
    <div style={s.lockedSlot}>
      <div style={s.lockIcon}>🔒</div>
      <div style={s.lockTitle}>Upgrade to add more</div>
      <div style={s.lockSub}>Pro: 6 slots · Elite: 15 slots</div>
      <button style={s.lockBtn} onClick={() => onUpgrade && onUpgrade()}>
        ⚡ Upgrade →
      </button>
    </div>
  )
}

const s = {
  page: { display: 'flex', flexDirection: 'column', paddingBottom: 40 },

  empty:      { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 30px', gap: 14 },
  emptyIcon:  { fontSize: 40, color: 'var(--accent, #c8a96e)' },
  emptyTitle: { fontSize: 16, fontWeight: 600, color: '#aaa' },
  emptySub:   { fontSize: 12, color: '#666', fontFamily: 'Courier New, monospace', textAlign: 'center' },

  profilePill: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'var(--bg-secondary, #111)', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  pillDot:     { width: 6, height: 6, borderRadius: '50%', background: '#4caf7d' },
  pillText:    { fontSize: 11, color: '#aaa', fontFamily: 'Courier New, monospace', letterSpacing: '0.04em' },

  section:      { padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  sectionLabel: { fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'Courier New, monospace', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  tierTag:      { fontSize: 9, color: '#c8a96e', background: 'rgba(200,169,110,0.1)', border: '1px solid rgba(200,169,110,0.25)', padding: '2px 8px', borderRadius: 999, letterSpacing: '0.08em' },

  slotGrid: { display: 'flex', flexDirection: 'column', gap: 10 },

  emptySlot: { display: 'flex', flexDirection: 'column' },
  emptyBtn:  { background: '#141414', border: '1px dashed rgba(200,169,110,0.3)', borderRadius: 9, padding: '14px', color: '#c8a96e', fontFamily: 'Courier New, monospace', fontSize: 12, cursor: 'pointer', letterSpacing: '0.04em', textAlign: 'center' },
  picker:    { background: '#0f0f0f', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 9, marginTop: 6, maxHeight: 280, overflowY: 'auto' },
  pickerEmpty: { padding: '14px', fontSize: 11, color: '#666', fontFamily: 'Courier New, monospace', textAlign: 'center' },
  pickerItem:  { display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '10px 12px', color: '#f0ede8', cursor: 'pointer', fontFamily: 'Courier New, monospace' },
  pickerItemName: { fontSize: 12, fontWeight: 600, color: '#f0ede8' },
  pickerItemMeta: { fontSize: 10, color: '#666', marginTop: 2 },

  slotCard:  { position: 'relative', background: '#141414', border: '1px solid rgba(200,169,110,0.3)', borderRadius: 9, padding: '12px 14px' },
  removeBtn: { position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', color: '#666', cursor: 'pointer', fontSize: 12, fontFamily: 'Courier New, monospace' },
  slotName:  { fontSize: 13, fontWeight: 600, color: '#f0ede8', fontFamily: 'Georgia, serif', paddingRight: 22 },
  slotMeta:  { fontSize: 10, color: '#888', fontFamily: 'Courier New, monospace', marginTop: 3 },

  lockedSlot: { background: '#0f0f0f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 9, padding: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  lockIcon:   { fontSize: 18 },
  lockTitle:  { fontSize: 12, color: '#aaa', fontFamily: 'Georgia, serif' },
  lockSub:    { fontSize: 10, color: '#555', fontFamily: 'Courier New, monospace' },
  lockBtn:    { marginTop: 6, background: '#c8a96e', color: '#0a0a0a', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'Georgia, serif' },

  hint: { padding: '14px 20px', fontSize: 11, color: '#666', fontFamily: 'Courier New, monospace', textAlign: 'center' },

  tableScroll: { overflowX: 'auto', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)', background: '#111' },
  table:       { width: '100%', borderCollapse: 'collapse', minWidth: 320 },
  thLabel:     { padding: '10px 12px', fontSize: 9, color: '#666', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Courier New, monospace', textAlign: 'left', background: '#0f0f0f', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  thRound:     { padding: '10px 12px', fontSize: 11, color: '#c8a96e', fontFamily: 'Courier New, monospace', textAlign: 'left', background: '#0f0f0f', borderBottom: '1px solid rgba(255,255,255,0.07)', minWidth: 140 },
  thRoundName: { fontWeight: 700, fontSize: 12, color: '#f0ede8' },
  thRoundMeta: { fontSize: 9, color: '#666', marginTop: 2 },
  tdLabel:     { padding: '10px 12px', fontSize: 10, color: '#666', fontFamily: 'Courier New, monospace', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  tdVal:       { padding: '10px 12px', fontSize: 12, color: '#f0ede8', fontFamily: 'Courier New, monospace', borderBottom: '1px solid rgba(255,255,255,0.05)' },
}
